import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectsQueryScope } from "@/features/projects/hooks/useProjectsQuery";
import { PERSONAL_PROJECT_SCOPE } from "@/features/projects/types";
import { useProjects } from "@/contexts/ProjectContext";
import type { MCPRequestOptions } from "@/services/agent-tools-service";
import type { MCPQueryScope } from "../query-keys";
import { mcpQueryKeys } from "../query-keys";

export const useMCPQueryScope = () => {
  const { scope, initialized } = useProjectsQueryScope();
  const { selectedProjects } = useProjects();
  const selection: string[] = Array.isArray(selectedProjects) ? selectedProjects : [];
  const all = selection.includes("all");
  // Arrays, not comma-separated IDs: explicit selections cannot collide with all.
  const projectIds = all ? [] : [...new Set(selection)].sort();
  const identity = JSON.stringify([scope.principalId, scope.organizationId, all, projectIds, initialized]);
  const result = useMemo(() => {
    const personal = scope.organizationId === PERSONAL_PROJECT_SCOPE;
    const key: MCPQueryScope = {
      principalId: initialized === true ? scope.principalId || null : null,
      workspace: personal ? { kind: "personal" } : { kind: "organization", id: scope.organizationId },
      projects: all ? { kind: "all" } : { kind: "selected", ids: projectIds },
    };
    return {
      key,
      projectIds,
      enabled: initialized === true && Boolean(scope.principalId),
      requestScope: Object.freeze({
        organizationId: personal ? null : scope.organizationId,
        projectId: !all && projectIds.length === 1 ? projectIds[0] : null,
      }),
      active: true,
      controller: new AbortController(),
    };
    // Identity includes every value consumed here; do not depend on context object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const previousScope = useRef(result);
  useLayoutEffect(() => {
    result.active = true;
    if (previousScope.current !== result) {
      previousScope.current.active = false;
      previousScope.current.controller.abort();
      previousScope.current = result;
    }
    // Do not cancel mutations merely because their component unmounted. Their
    // hook-level cache settlement must still run in the originating scope.
    return () => { result.active = false; };
  }, [result]);
  return result;
};

export type MCPQueryContext = ReturnType<typeof useMCPQueryScope>;
export const mcpRequestOptions = (scope: MCPQueryContext, signal?: AbortSignal): MCPRequestOptions => ({
  requestScope: scope.requestScope,
  signal: signal ?? scope.controller.signal,
});

// OAuth watchers may complete after a workspace switch; retain their origin.
export const useInvalidateMCPConnections = () => {
  const scope = useMCPQueryScope();
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: mcpQueryKeys.scope(scope.key) }), [scope, queryClient]);
};
